import { UserManager, User } from "oidc-client-ts";
import { getOidcConfig } from "src/config/oidcConfig";

class OIDCAuth {
  private static instance: OIDCAuth;
  private userManager: UserManager;
  private readonly FEDERATE_TOKEN = "federate_token";

  private constructor() {
    this.userManager = new UserManager(getOidcConfig());
  }

  private cacheUserToken(user: User): void {
    if (user?.id_token)
      localStorage.setItem(this.FEDERATE_TOKEN, user.id_token);
  }

  private cleanupTemporaryOidcKeys(): void {
    // Find temporary OIDC state keys (long random IDs, but preserve user data)
    const tempKeys = Object.keys(localStorage).filter(
      (key) => key.startsWith("oidc.") && !key.includes("user"),
    );

    tempKeys.forEach((key) => localStorage.removeItem(key));
  }

  public static getInstance(): OIDCAuth {
    if (!OIDCAuth.instance) {
      OIDCAuth.instance = new OIDCAuth();
    }
    return OIDCAuth.instance;
  }

  private shouldRenewToken(token: string, bufferMinutes: number = 5): boolean {
    /*
      making sure federate id_token has expiry of more than 5 minutes, otherwise
      we will trigger a call to federate for new id_token
    */

    try {
      const payload = JSON.parse(window.atob(token.split(".")[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime + bufferMinutes * 60;
    } catch (error) {
      console.log("Error parsing token:", error);
      return true;
    }
  }

  public async getFederateToken(
    useMainWindowAuth: boolean = false,
  ): Promise<User | null> {
    try {
      const existingToken = localStorage.getItem(this.FEDERATE_TOKEN);
      const user = await this.userManager.getUser();

      // Check if the token is valid, if yes then return the same token.
      if (user && existingToken && !this.shouldRenewToken(existingToken)) {
        return user;
      }

      if (useMainWindowAuth) await this.renewViaMainWindow(); // Browser redirects here - execution stops

      return await this.renewViaSilentIframe();
    } catch (error) {
      console.error("Token acquisition failed:", error);
      return null;
    }
  }

  // This creates a hidden Iframe to talk to federate under the hood.
  private async renewViaSilentIframe(): Promise<User | null> {
    try {
      await this.logout();
      console.log("Renewing token via silent iframe...");

      const user = await this.userManager.signinSilent();

      if (user) this.cacheUserToken(user);

      return user;
    } catch (error) {
      /*
        if the iframe time out, assuming it got stuck on midway login page, 
        so this redirect the user to mainwindow to login for midway.
      */
      console.error("Silent renewal failed:", error);
      await this.getFederateToken(true); // true means it will do auth process using main-window.
    }
  }

  /*
    If in case midway session expires, user will be redirected to maindow to peform midway
    authentication again.
  */
  private async renewViaMainWindow(): Promise<void> {
    try {
      await this.logout(); // this steps remove user from local storage completely to perform full auth process.
      console.log("Redirecting to main window auth...");

      const state = window.location.href; //store the state(url) before login, so we can redirect to same page after authentication is done.
      await this.userManager.signinRedirect({ state });
    } catch (error) {
      console.error("Main window auth failed:", error);
      throw error;
    }
  }

  public async handleMainWindowCallback(): Promise<User | null> {
    try {
      const user = await this.userManager.signinRedirectCallback();

      if (user) {
        this.cacheUserToken(user);
      }

      this.cleanupTemporaryOidcKeys();
      window.location.href = user.state as string;
      return user;
    } catch (error) {
      console.error("Main window callback failed:", error);
      return null;
    }
  }

  public async checkAndGetValidToken(): Promise<string | null> {
    try {
      const existingToken = localStorage.getItem(this.FEDERATE_TOKEN);

      if (!existingToken) return null;

      const user = await this.userManager.getUser();

      if (user && !this.shouldRenewToken(existingToken)) return existingToken;

      const renewedUser = await this.renewViaSilentIframe();

      return renewedUser?.id_token || null;
    } catch (error) {
      return null;
    }
  }

  public async logout(): Promise<void> {
    try {
      localStorage.removeItem(this.FEDERATE_TOKEN);
      await this.userManager.removeUser();
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  public getUserManager(): UserManager {
    return this.userManager;
  }
}

export const Auth = OIDCAuth.getInstance();

export default Auth;
